import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class TranslationLoaderService {
  private loadedPaths = new Map<string, Observable<any>>();
  private translationsLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  load(lang: string, path: string): Observable<any> {
    const fullPath = `/i18n/${lang}/${path}.json`;
    const cacheKey = `${lang}|${path}`;

    if (this.loadedPaths.has(cacheKey)) {
      return this.loadedPaths.get(cacheKey)!;
    }

    const request$ = this.http.get(fullPath).pipe(
      map((res: any) => {
        if (res && typeof res === 'object') {
          this.translate.setTranslation(lang, res, true);
          return res;
        }
        console.warn(`Invalid or empty translation file: ${fullPath}`);
        return {};
      }),
      catchError((err) => {
        console.warn(`Missing translation file: ${fullPath}`, err);
        return of({});
      }),
      tap(() => {
        this.loadedPaths.set(cacheKey, of({}));
        this.translationsLoaded$.next(true);
      })
    );

    this.loadedPaths.set(cacheKey, request$);
    return request$;
  }

  /** Every translation file the app uses. Add new files here so they load for each language. */
  static readonly FILES = [
    'common/common',
    'auth/navbar',
    'auth/login',
    'auth/signup',
    'auth/forgot',
    'auth/reset-password',
    'auth/otp',
    'auth/onboarding',
    'auth/premium-cars',
    'views/home',
  ];

  loadAll(lang: string): Observable<any[]> {
    return forkJoin(TranslationLoaderService.FILES.map((path) => this.load(lang, path)));
  }

  isTranslationsLoaded(): Observable<boolean> {
    return this.translationsLoaded$.asObservable();
  }
}
